import React from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { getCanonicalOrigin } from '../../util/domain';
import { logoCabecalho } from '../../util/logoLoja';

/**
 * HelmetCustom — SEO meta tags dinâmicas por tenant e por página.
 *
 * Props aceitas (todas opcionais — fallback vem da empresa/identidade do Redux):
 *   pageTitle (segmento da página; compõe "pageTitle | marca"),
 *   fbTitle (override total do título), content, fbDescription, fbImage, fbUrl,
 *   canonical, name, ogType ("website" | "product" | ...),
 *   twitterCard ("summary_large_image" | "summary"), noindex (bool),
 *   keywords (string | string[]), children
 *
 * Título (fonte única): fbTitle > (pageTitle → "pageTitle | marca") > titulo_site > marca.
 */
function HelmetCustom(props) {
  const empresa = useSelector(state => state.store.data?.empresa);
  const identidade = useSelector(state => state.identidadeVisual?.data);

  const origin = getCanonicalOrigin(empresa?.url_canonica, window.location.origin);
  const currentUrl = `${origin}${window.location.pathname}`;
  const isProduction = process.env.REACT_APP_ENV === 'production';

  const brand = empresa?.nome || '';
  const siteTitle = identidade?.titulo_site || brand;
  const title =
    props.fbTitle
    || (props.pageTitle
        ? (brand ? `${props.pageTitle} | ${brand}` : props.pageTitle)
        : siteTitle);
  const description = props.content || props.fbDescription
    || identidade?.descricao_site || empresa?.descricao_site || '';
  const siteName = empresa?.nome || '';
  const ogImage = props.fbImage || logoCabecalho(identidade, empresa) || '';
  const ogType = props.ogType || 'website';
  // Força noindex em ambientes não-produtivos, independente da prop
  const robotsContent = (!isProduction || props.noindex) ? 'noindex, nofollow' : 'index, follow, max-image-preview:large';
  const twitterCard = props.twitterCard || 'summary_large_image';
  const keywords = Array.isArray(props.keywords) ? props.keywords.join(', ') : props.keywords;

  return (
    <Helmet>
      {props.children}
      {title && <title>{title}</title>}
      <meta name={props.name || "description"} content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={props.canonical || currentUrl} />

      {/* Open Graph */}
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={props.fbUrl || currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="800" />
      <meta property="og:image:height" content="600" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}

export default HelmetCustom;
